/**
 * @file enqueueDesktopPinWrite.ts
 * @description Serializes every pin write for one user document. Consumers:
 * `keep` and `stopKeeping`, which are the only writers of `desktopPins`.
 *
 * Both read the current map off the store and write a round trip later, so two
 * un-awaited calls would each read a map without the other's entry. The visible
 * failure is the one this whole layer exists to prevent: two fast clicks on an
 * unpinned artifact each mint a fresh `uuidv4()` and leave two entries for one
 * path. Serializing here rather than asking four UI implementers to await is
 * the difference between one place to get it right and four to get it wrong.
 *
 * One chain per user, not per path, because `desktopPinPaths` is recomputed
 * from the whole map — writes to different paths race on the array even when
 * their pin entries do not. A rejected write does not wedge the chain: the next
 * entry runs whether its predecessor settled or failed, and the stored tail
 * never rejects, so no chain link becomes an unhandled rejection. The map holds
 * one entry per user with a write in flight and drops it when the tail settles.
 */

const chains = new Map<string, Promise<void>>();

export const enqueueDesktopPinWrite = (
    userId: string,
    write: () => Promise<void>,
): Promise<void> => {
    const previous = chains.get(userId) ?? Promise.resolve();
    const result = previous.then(write, write);
    const tail = result.then(() => undefined, () => undefined);

    chains.set(userId, tail);
    void tail.then(() => {
        if (chains.get(userId) === tail) chains.delete(userId);
    });

    return result;
};
